import React, { useState, useMemo } from 'react'
import { useAuthStatus } from '../../utils'
import { useContractList } from '../../hooks'
import styles from './ContractManagement.module.scss'
import ContractItem from './components/ContractItem'
import SubTabMenu from './components/SubTabMenu'
import Pagination from './components/Pagination'

export const ContractManagementListView = ({ ...props }) => {
	const [auth] = useAuthStatus()
	const [subSelectedTab, setSubSelectedTab] = useState('all')
	const [currentPage, setCurrentPage] = useState(1)
	const [currentPageGroupStart, setCurrentPageGroupStart] = useState(1)

	const itemsPerPage = 5
	const pageGroupSize = 5

	// React Query 훅으로 계약 목록 조회
	const { data: contracts = [], isLoading, isError, error } = useContractList()

	// 서브 탭별 필터링
	const filteredContracts = useMemo(() => {
		if (!Array.isArray(contracts)) return []

		switch (subSelectedTab) {
			case 'wait':
				return contracts.filter((item) => item.contract_status === 'PRE')
			case 'complete':
				return contracts.filter((item) => item.contract_status === 'ACCEPTED')
			case 'refuse':
				return contracts.filter((item) => item.contract_status === 'CANCELED')
			case 'earlyTermination':
				return contracts.filter((item) => item.contract_status === 'TERMINATED')
			default:
				return contracts
		}
	}, [contracts, subSelectedTab])

	// 서브 탭별 개수 계산
	const counts = useMemo(() => {
		if (!Array.isArray(contracts)) {
			return {
				allCount: 0,
				waitCount: 0,
				completeCount: 0,
				refuseCount: 0,
				earlyTerminationCount: 0,
			}
		}

		return {
			allCount: contracts.length,
			waitCount: contracts.filter((item) => item.contract_status === 'PRE').length,
			completeCount: contracts.filter((item) => item.contract_status === 'ACCEPTED').length,
			refuseCount: contracts.filter((item) => item.contract_status === 'CANCELED').length,
			earlyTerminationCount: contracts.filter((item) => item.contract_status === 'TERMINATED')
				.length,
		}
	}, [contracts])

	// 페이지네이션
	const totalPages = Math.ceil(filteredContracts.length / itemsPerPage)
	const indexOfLastContract = currentPage * itemsPerPage
	const indexOfFirstContract = indexOfLastContract - itemsPerPage
	const currentContracts = filteredContracts.slice(indexOfFirstContract, indexOfLastContract)

	const handleSubSelectedTab = (selectedTab) => {
		setSubSelectedTab(selectedTab)
		setCurrentPage(1)
		setCurrentPageGroupStart(1)
	}

	const handlePageChange = (pageNumber) => {
		setCurrentPage(pageNumber)
	}

	const handleNextGroup = () => {
		const newStartPage = currentPageGroupStart + pageGroupSize
		setCurrentPage(newStartPage)
		setCurrentPageGroupStart(newStartPage)
	}

	const handlePrevGroup = () => {
		const newStartPage = currentPageGroupStart - pageGroupSize
		setCurrentPage(newStartPage)
		setCurrentPageGroupStart(newStartPage)
	}

	const saveContractIdToSessionStorage = (contractId) => {
		sessionStorage.setItem('ContractManagementContractId', contractId)
	}

	return (
		<div className={styles.container}>
			<div className={styles.title}>
				<div className={styles.bigTitle}>계약 관리</div>
				<div className={styles.smallTitle}>계약 정보 및 현황을 확인해보세요</div>
			</div>

			<div className={styles.contractList}>
				<SubTabMenu
					subSelectedTab={subSelectedTab}
					onSubTabChange={handleSubSelectedTab}
					counts={counts}
				/>

				{isLoading && <div className={styles.loadingMessage}>로딩 중...</div>}
				{isError && (
					<div className={styles.errorMessage}>
						{error?.message || '계약 정보를 불러오는 데 실패했습니다.'}
					</div>
				)}
				{!isLoading && !isError && currentContracts.length === 0 && (
					<div className={styles.noContractsMessage}>표시할 계약 정보가 없습니다.</div>
				)}
				{!isLoading &&
					!isError &&
					currentContracts.length > 0 &&
					currentContracts.map((contract) => (
						<div key={contract.id} className={styles.contractItem}>
							<ContractItem
								contract={contract}
								onContractClick={saveContractIdToSessionStorage}
							/>
						</div>
					))}
			</div>

			<Pagination
				currentPage={currentPage}
				totalPages={totalPages}
				pageGroupSize={pageGroupSize}
				currentPageGroupStart={currentPageGroupStart}
				onPageChange={handlePageChange}
				onPrevGroup={handlePrevGroup}
				onNextGroup={handleNextGroup}
			/>
		</div>
	)
}
